import { useMemo, useRef, useLayoutEffect, useCallback, useState } from "react";
import { MdCheckCircle, MdOutlineSchedule, MdAnalytics, MdDownload } from "react-icons/md";
import { IconType } from "react-icons/lib";
import { Button } from "antd";

import useModal from "../../../../hooks/useRelatorioContext";
import { useRelatorioDetalheAderente, useSheduleDetailExport } from "../../../../hooks/useRelatorio";
import columns from "./columns";
import { Icon } from "../../../Icon";
import { Loader } from "../../partials/Loader";
import {
  BODY_VERTICAL_PADDING,
  ButtonExportExcel,
  ContainerFooter,
  ContainerTable,
  CustomModal,
  HeaderWrapper,
  MODAL_CHROME_HEIGHT,
  ModalContainer,
  ModalTableContentWrap,
  RoundedTable,
  SummaryCard,
  SummaryContent,
  SummaryIcon,
  SummaryLabel,
  SummaryValue,
  TABLE_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from "./styles";

function ModalDetalheTalhao() {
  const summaryRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const {
    isOpen,
    details,
    setIsOpen,
    setDetails,
    toleranciaEmDias,
    tipoPlanejamento,
  } = useModal();
  const { mutateAsync: exportShedule } = useSheduleDetailExport(details?.talhaoId!);

  const { data, isFetching } = useRelatorioDetalheAderente(details?.talhaoId!, toleranciaEmDias, tipoPlanejamento);

  const dataSource = useMemo(() => {
    if (isFetching) return [];
    if (!data) return [];
    const sorted = [...data].sort((a: any, b: any) => {
      const da = a?.dataProgramada
        ? new Date(a.dataProgramada).getTime()
        : Infinity

      const db = b?.dataProgramada
        ? new Date(b.dataProgramada).getTime()
        : Infinity

      return da - db
    });

    sorted.forEach((item, index: number) => {
      item.index = index + 1;
    });

    return sorted;
  }, [isFetching, data]);

  const updateTableScroll = useCallback(() => {
    const summaryHeight = summaryRef.current?.offsetHeight ?? 0;
    const maxBodyHeight = window.innerHeight * 0.9 - MODAL_CHROME_HEIGHT;
    const naturalTableHeight = TABLE_HEADER_HEIGHT + dataSource?.length * TABLE_ROW_HEIGHT;
    const naturalBodyHeight = summaryHeight + BODY_VERTICAL_PADDING + naturalTableHeight;

    if (naturalBodyHeight <= maxBodyHeight) {
      setTableScrollY(undefined);
      return;
    }

    const scrollArea = maxBodyHeight - summaryHeight - BODY_VERTICAL_PADDING - TABLE_HEADER_HEIGHT;
    setTableScrollY(Math.max(scrollArea, 100));
  }, [dataSource?.length]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const run = () => {
      requestAnimationFrame(() => {
        setTimeout(updateTableScroll, 30);
      });
    };

    run();
    window.addEventListener("resize", updateTableScroll);

    return () => window.removeEventListener("resize", updateTableScroll);
  }, [isOpen, updateTableScroll]);

  const resumoTalhao = useMemo(() => {
    const operacoes = data ?? [];
    const aderente = operacoes.filter((item: any) => item.aderente === true).length;
    const naoAderente = operacoes.filter((item: any) => item.aderente === false).length;
    const totalAvaliado = aderente + naoAderente;
    const porcentagem = totalAvaliado > 0 ? Math.round((aderente / totalAvaliado) * 100) : 0;

    const counterMedia = operacoes.filter((item: any) => item.status === "Executada" && Math.abs(item.quantidadeDias) > 0)?.length;
    const mediaDesvio = counterMedia > 0
      ? operacoes
        .filter((item: any) => item.status === "Executada")
        .reduce((sum: number, item: any) => sum + Math.abs(item.quantidadeDias ?? 0), 0) / counterMedia
      : 0;

    return {
      porcentagem,
      aderente,
      naoAderente,
      mediaDesvio: Math.abs(Number(mediaDesvio.toFixed(1))).toLocaleString("pt-BR"),
    };
  }, [data]);

  const summaryCards: {
    icon: IconType;
    label: string;
    value: string | number;
    color: string;
    backgroundColor: string;
  }[] = [
      {
        icon: MdCheckCircle,
        label: "Aderência",
        value: `${resumoTalhao.porcentagem}%`,
        color: "#16a34a",
        backgroundColor: "#dcfce7",
      },
      {
        icon: MdOutlineSchedule,
        label: "Operações Executadas no Prazo",
        value: resumoTalhao.aderente,
        color: "#2563eb",
        backgroundColor: "#dbeafe",
      },
      {
        icon: MdOutlineSchedule,
        label: "Operações Executadas Fora do Prazo",
        value: resumoTalhao.naoAderente,
        color: "#dc2626",
        backgroundColor: "#fee2e2",
      },
      {
        icon: MdAnalytics,
        label: "Média de Desvio (dias)",
        value: resumoTalhao.mediaDesvio,
        color: "#d97706",
        backgroundColor: "#fef3c7",
      },
    ];

  function closedModal() {
    setIsOpen(false);
    setDetails(null);
  }

  async function handleExportExcel() {
    setLoading(true);
    const query = { talhaoId: details?.talhaoId, tipo: tipoPlanejamento, ToleranciaEmDias: toleranciaEmDias };
    const blob = await exportShedule(query);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "arquivo.xls";
    link.click();
    setLoading(false);
  }

  return (
    <CustomModal
      title={`Informações do talhão ${details?.code ?? ''}`}
      visible={isOpen}
      onCancel={closedModal}
      centered
      zIndex={1001}
      width={"80%"}
      maskClosable={false}
      footer={
        <ContainerFooter>
          <Button
            size="large"
            onClick={closedModal}
            style={{ borderRadius: 8 }}
          >Fechar</Button>
          <ButtonExportExcel disabled={loading} onClick={handleExportExcel}><MdDownload /> {loading ? "Carregando..." : "Exportar Excel"} </ButtonExportExcel>
        </ContainerFooter>
      }
    >
      {isFetching
        ? <Loader /> : (
          <ModalContainer $hasScroll={!!tableScrollY}>
            <HeaderWrapper ref={summaryRef}>
              {summaryCards.map((card) => (
                <SummaryCard key={card.label}>
                  <SummaryIcon color={card.color} backgroundColor={card.backgroundColor}>
                    <Icon icon={card.icon} />
                  </SummaryIcon>
                  <SummaryContent>
                    <SummaryValue>{card.value}</SummaryValue>
                    <SummaryLabel>{card.label}</SummaryLabel>
                  </SummaryContent>
                </SummaryCard>
              ))}
            </HeaderWrapper>
            <ModalTableContentWrap $hasScroll={!!tableScrollY}>
              <ContainerTable $hasScroll={!!tableScrollY}>
                <RoundedTable
                  style={{ marginBottom: 16 }}
                  columns={columns(tipoPlanejamento)}
                  dataSource={dataSource}
                  className="my-table"
                  scroll={tableScrollY ? { y: tableScrollY } : undefined}
                  sticky={!!tableScrollY}
                  pagination={false}
                />
              </ContainerTable>
            </ModalTableContentWrap>
          </ModalContainer>
        )}
    </CustomModal>
  );
}

export default ModalDetalheTalhao;